import React, { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash/debounce';

import './CompanySearch.css';
import { apiClient, API_CONFIG } from '../api/config';
import { useAuth } from '../user/auth/AuthContext';
import LoginDialog from '../user/auth/LoginDialog';
import activityTracker from '../user/ActivityTracker';
import { logger } from '../utils/logger';
import { useLocation } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';

const CompanySearch = ({ onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isNavigating, setIsNavigating] = useState(false);
  const searchRef = useRef(null);
  
  // 添加一个状态来控制是否显示清除按钮
  const [showClearButton, setShowClearButton] = useState(false);

  // 获取认证状态
  const { isAuthenticated } = useAuth();

  // 引用和获取搜索次数
  const getSearchCount = () => {
    const currentCount = parseInt(localStorage.getItem('companySearchCount') || '0');
    const lastResetTime = localStorage.getItem('companySearchCountTime');
    const currentTime = new Date().getTime();
    
    // 如果没有时间戳或超过4天，重置计数
    if (!lastResetTime || (currentTime - parseInt(lastResetTime)) > (4 * 24 * 60 * 60 * 1000)) {
      localStorage.setItem('companySearchCount', '0');
      localStorage.setItem('companySearchCountTime', currentTime.toString());
      return 0;
    }
    
    return currentCount;
  };
  
  // 增加搜索计数
  const incrementSearchCount = () => {
    if (!isAuthenticated) {
      const currentCount = getSearchCount();
      const currentTime = new Date().getTime();
      localStorage.setItem('companySearchCount', (currentCount + 1).toString());
      localStorage.setItem('companySearchCountTime', currentTime.toString());
      return currentCount + 1;
    }
    return 0; // 已登录用户不计数
  };
  
  // 检查是否超过限制
  const checkSearchLimit = () => {
    const currentCount = getSearchCount();
    logger.log('当前搜索次数:', currentCount); // 添加日志
    
    if (!isAuthenticated && currentCount >= 3) {
      logger.log('超过限制，显示登录对话框'); // 添加日志
      setShowLoginPrompt(true);
      return true;
    }
    return false;
  };

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);


  const location = useLocation();

  const { updateCompanyInfo } = useCompany();

  // 使用 debounce 处理搜索
  const debouncedSearch = useCallback(
    debounce(async (term) => {
      if (term.length < 2) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await apiClient.get(API_CONFIG.endpoints.companySearch, {
          params: { term }
        });
        setSuggestions(response.data);
      } catch (error) {
        logger.error('搜索出錯:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (!isNavigating) {
      debouncedSearch(searchTerm);
    }
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch, isNavigating]);

  // 当输入变化时，更新清除按钮的显示状态
  useEffect(() => {
    setShowClearButton(searchTerm.length > 0);
  }, [searchTerm]);

  useEffect(() => {
    // 添加点击外部关闭下拉框的处理
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleKeyDown = (e) => {
    if (!suggestions.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setIsNavigating(true);
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setIsNavigating(true);
        setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          // 如果未登录且已达到限制，显示登录对话框
          if (!isAuthenticated && getSearchCount() >= 3) {
            setShowLoginPrompt(true);
            return;
          }
          handleCompanySelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setSuggestions([]);
        setSelectedIndex(-1);
        setShowSuggestions(false);
        setIsNavigating(false);
        break;
      default:
        setIsNavigating(false);
        break;
    }
  };

  const handleCompanySelect = (company) => {
    // 先增加计数，然后检查限制
    if (!isAuthenticated) {
      const newCount = incrementSearchCount();
      logger.log('新的搜索次数:', newCount); // 添加日志

      // 检查后显示限制版登录对话框
      const showLimitLoginDialog = () => {
        logger.log('显示限制版登录对话框');
        setShowLoginPrompt(true);
      };

      // 在达到限制时调用这个函数
      if (newCount >= 3) {
        logger.log('达到限制，显示限制版登录对话框');
        showLimitLoginDialog();
        return;
      }
    }

    // 使用 CompanyContext 更新公司信息
    updateCompanyInfo(company);
    
    // 追蹤公司選擇行為
    activityTracker.trackBtnExecution('company_search', company.symbol, location.pathname);

    // 調用傳入的 onSelect 回調函數
    if (onSelect) {
      onSelect(company);
    }

    // 清空搜索框和建议
    setSearchTerm('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  // 添加清除输入的函数
  const handleClearInput = (e) => {
    e.stopPropagation(); // 阻止事件冒泡
    setSearchTerm('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setIsNavigating(false);
  };


  // 假设这是处理表单提交的函数
  const handleSubmit = (e) => {
    e.preventDefault();
    // 仅在按下 Enter 键搜索时记录
    activityTracker.trackSearch(searchTerm);
    // ... 其他搜索逻辑
  };

  const [symbol, setSymbol] = useState('');

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSymbol(value.toUpperCase());
    setShowSuggestions(true);
    setSelectedIndex(-1);
    setIsNavigating(false);
  };

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchTerm.length < 2) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);

      try {
        const response = await apiClient.get(API_CONFIG.endpoints.companySearch, {
          params: { term: searchTerm }
        });
        setSuggestions(response.data);
      } catch (err) {
        logger.error('搜索出錯:', err);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleSearch = async (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      e.preventDefault();
      setIsLoading(true);
      
      try {
        const response = await apiClient.get(API_CONFIG.endpoints.companySearch, {
          params: { term: searchTerm.trim() }
        });
        
        if (response.data && response.data.length > 0) {
          const selectedCompany = response.data[0];
          
          // 使用 CompanyContext 更新公司信息
          updateCompanyInfo(selectedCompany);
          
          // 調用傳入的 onSelect 回調函數
          if (onSelect) {
            onSelect(selectedCompany);
          }
          
          // 清除搜索状态
          setSearchTerm('');
          setSuggestions([]);
          setShowSuggestions(false);
          setShowClearButton(false);
        }
      } catch (error) {
        logger.error('搜索公司失败:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };


  return (
    <div className="company-search" ref={searchRef}>
      <div className="search-input-container">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            // 处理回车搜索
            if (e.key === 'Enter' && !suggestions.length) {
              handleSearch(e);
            }
            // 处理键盘导航
            handleKeyDown(e);
          }}
          onFocus={() => setShowSuggestions(true)}
          placeholder="輸入公司名稱或股票代碼..."
          className="search-input"
          required
        />
        {/* 添加清除按钮 */}
        {showClearButton && (
          <button 
            className="clear-button" 
            onClick={handleClearInput}
            aria-label="清除搜索"
          >
            ×
          </button>
        )}
        {isLoading && <div className="loading-indicator">搜索中...</div>}
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <ul className="suggestions-list">
          {suggestions.map((company, index) => (
            <li
              key={company.symbol}
              onClick={() => handleCompanySelect(company)}
              className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
              onMouseEnter={() => {
                setSelectedIndex(index);
                setIsNavigating(true);
              }}
            >
              <div className="company-info">
                <img 
                  src={company.image || '/default-company-icon.png'} 
                  alt={`${company.company_name} logo`}
                  className="company-icon"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/default-company-icon.png';
                  }}
                />
                <span className="company-symbol">{company.symbol}</span>
                <span className="company-name">{company.company_name}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
      
      {/* 登录提示对话框 */}
      <LoginDialog 
        open={showLoginPrompt} 
        onClose={() => {
          logger.log('关闭登录对话框');
          setShowLoginPrompt(false);
        }}
        openRegister={() => {
          setShowLoginPrompt(false);
        }}
        isLimitReached={true} // 确保这个属性是 true
      />
    </div>
  );
};

export default CompanySearch; 